import { env } from "@/lib/env"
import { tokenManager } from "@/lib/token-manager"
import type { AuditResponse, AuditSearchParams, AuditHistoryDocument } from "@/types/audit"

export class AuditService {
  private static readonly baseUrl = env.auditApiUrl

  static async getAuditHistory(
    itemId: string,
    searchParams: AuditSearchParams
  ): Promise<AuditHistoryDocument[]> {
    const groupId = `WorkflowHubReport-${itemId}`
    const url = new URL(`${this.baseUrl}/Audit/${groupId}`)
    
    // Add query parameters
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.append(key, value.toString())
      }
    })

    const token = tokenManager.getLastToken()
    if (!token) {
      throw new Error("No authentication token available")
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Audit API request failed with status ${response.status}`)
    }

    const data = await response.json()
    
    // The API returns an array directly, not wrapped in an object
    if (Array.isArray(data)) {
      return data as AuditHistoryDocument[]
    }
    
    // Fallback: if it's wrapped in an object, extract the records
    if (data && Array.isArray(data.records)) {
      return data.records as AuditHistoryDocument[]
    }
    
    // If neither, return empty array
    console.warn('Unexpected audit API response format:', data)
    return []
  }

  static async getAuditHistoryForItem(
    itemId: string,
    page: number = 1,
    pageSize: number = 20,
    searchTerm?: string
  ): Promise<AuditHistoryDocument[]> {
    const searchParams: AuditSearchParams = {
      page,
      pageSize,
      searchTerm,
    }

    return this.getAuditHistory(itemId, searchParams)
  }
}
